import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/Auth';
import { getApiUrl } from '../../config/api';
import styles from './SuccessPage.module.css';

const TrialSuccessPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { updateUser } = useAuth();
  const [trialData, setTrialData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [countdown, setCountdown] = useState(3);
  
  // Prevent duplicate API calls in React 18 Strict Mode
  const hasConfirmed = React.useRef(false);

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    // Skip if already confirmed
    if (hasConfirmed.current) {
      return;
    }

    if (sessionId) {
      hasConfirmed.current = true;
      confirmTrialActivation();
    } else {
      setError('No session ID found');
      setLoading(false);
    }
  }, [sessionId]);

  // Countdown timer for auto-redirect
  useEffect(() => {
    if (trialData && countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (trialData && countdown === 0) {
      navigate('/dashboard');
    }
  }, [trialData, countdown, navigate]);

  const confirmTrialActivation = async () => {
    try {
      const token = localStorage.getItem('authToken');
      
      if (!token) {        throw new Error('No auth token found. Please login again.');
      }

      const confirmUrl = getApiUrl('/billing/confirm-trial');
      
      const confirmResponse = await fetch(confirmUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ sessionId }),
      });

      if (confirmResponse.ok) {
        const data = await confirmResponse.json();
        
        // Update user in context with trial status
        if (data.user) {
          updateUser(data.user);
        }
        
        setTrialData(data);
      } else {
        const errorText = await confirmResponse.text();
        console.error('Failed to confirm trial:', errorText);
        throw new Error('Failed to activate trial');
      }
    } catch (err) {
      console.error('Trial confirmation error:', err);
      setError(err instanceof Error ? err.message : 'Failed to activate trial');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const calculateDaysRemaining = (endDate: string) => {
    const end = new Date(endDate);
    const now = new Date();
    const diffTime = Math.abs(end.getTime() - now.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingWrapper}>
          <div className={styles.spinner}></div>
          <p className={styles.loadingText}>Activating your trial...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.container}>
        <div className={styles.errorWrapper}>
          <div className={styles.errorIcon}>❌</div>
          <h1 className={styles.errorTitle}>Activation Failed</h1>
          <p className={styles.errorText}>{error}</p>
          <button 
            className={styles.button}
            onClick={() => navigate('/pricing')}
          >
            Return to Pricing
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.successWrapper}>
        {/* Success Animation */}
        <div className={styles.checkmarkWrapper}>
          <div className={styles.checkmarkCircle}>
            <div className={styles.checkmark}>✓</div>
          </div>
        </div>

        {/* Trial Activated Message */}
        <h1 className={styles.successTitle}>🎉 Trial Activated!</h1>
        <p className={styles.successSubtitle}>
          You have <strong>{trialData?.daysRemaining || 7} days</strong> and{' '}
          <strong>{trialData?.pointsGranted || 3500} points</strong> to explore everything
        </p>

        {/* Trial Details Card */}
        <div className={styles.trialDetailsCard}>
          <div className={styles.detailRow}>
            <span className={styles.detailLabel}>Trial Ends:</span>
            <span className={styles.detailValue}>
              {trialData?.trialEndsAt ? formatDate(trialData.trialEndsAt) : 'N/A'}
            </span>
          </div>
          <div className={styles.detailRow}>
            <span className={styles.detailLabel}>Points Available:</span>
            <span className={styles.detailValue}>{trialData?.pointsGranted || 3500}</span>
          </div>
          <div className={styles.detailRow}>
            <span className={styles.detailLabel}>Plan Features:</span>
            <span className={styles.detailValue}>All Creator Lite features</span>
          </div>
          <div className={styles.detailRow}>
            <span className={styles.detailLabel}>After Trial:</span>
            <span className={styles.detailValue}>Upgrades to 7,000 points/mo</span>
          </div>
        </div>

        {/* Auto-Renewal Disclosure */}
        <div className={styles.autoRenewDisclosure}>
          <div className={styles.disclosureIcon}>⚠️</div>
          <div className={styles.disclosureContent}>
            <h3>Auto-Renewal Notice</h3>
            <p>
              Your trial converts to <strong>Creator Lite ($24.99/mo)</strong> on{' '}
              <strong>{trialData?.trialEndsAt ? formatDate(trialData.trialEndsAt) : 'trial end date'}</strong>.
            </p>
            <p>
              You can cancel anytime before then to avoid charges in{' '}
              <span 
                className={styles.linkText}
                onClick={() => navigate('/settings/subscription')}
              >
                Settings → Subscription
              </span>
            </p>
          </div>
        </div>

        {/* Call to Action */}
        <button 
          className={styles.primaryButton}
          onClick={() => navigate('/dashboard')}
        >
          Start Creating Now
        </button>

        {/* Auto-redirect countdown */}
        <p className={styles.countdownText}>
          Redirecting to dashboard in {countdown} seconds...
        </p>

        <button 
          className={styles.secondaryButton}
          onClick={() => navigate('/pricing')}
        >
          View All Plans
        </button>
      </div>
    </div>
  );
};

export default TrialSuccessPage;
